import { useState, useEffect, useMemo } from "react";
import { useLocalization } from "@fluent/react";
import cc from "classcat";
import { formatNumber, Tag } from "@narmi/design_system";
import Filters from "byzantine/src/filters";
import Transaction, { isABalance } from "byzantine/src/Transaction";
import NewThreadDialog from "./NewThreadDialog";
import TransactionDetailsDialog from "./TransactionDetails";
import styles from "./RecentTransactionsTable.module.scss";

interface EmptyTransactionStateProps {
  isSourceMultipleAccounts: boolean;
}

interface TransactionRowProps extends EmptyTransactionStateProps {
  transaction: Transaction;
  replyTime: string;
}

type TransactionRawMetadata = {
  metadata?: {
    should_show_ledger_balance?: {
      value?: string;
    };
  };
};

const TransactionRow = ({
  isSourceMultipleAccounts,
  transaction,
  replyTime
}: TransactionRowProps) => {
  const { l10n } = useLocalization();
  const {
    account,
    creation_date: creationDate,
    description,
    amount,
    resulting_balance: resultingBalance,
    posted_date: postedDate
  } = transaction;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReportIssueDialogOpen, setIsReportIssueDialogOpen] = useState(false);
  const [displayDescription, setDisplayDescription] = useState(description);

  useEffect(() => {
    setDisplayDescription(description);
  }, [description]);

  // need to tack on a `+` sign if the amount is positive since NDS.formatNumber does not do that for us
  const displayedAmount = `${amount > 0 ? "+" : ""}${formatNumber(
    amount / 100,
    "currency"
  )}`;
  const raw = transaction.raw as TransactionRawMetadata;
  const shouldShowLedgerBalance =
    raw.metadata?.should_show_ledger_balance?.value === "true";
  // Display resulting balance for settled transactions and posted transactions
  // that are temporarily rendered as pending.
  const displayedResultingBalance =
    (postedDate || shouldShowLedgerBalance) && isABalance(resultingBalance)
      ? formatNumber((resultingBalance as number) / 100)
      : null;

  const isBillPayTransaction = useMemo(
    () => transaction.source === "bill_payment",
    [transaction.source]
  );

  const datePrefix = useMemo(
    () =>
      isBillPayTransaction
        ? l10n.getString("bill-payment-expected-arrival-date", {}, "")
        : "",
    [isBillPayTransaction, l10n]
  );

  const formattedDate = Filters.longMonthDayYear(postedDate || creationDate);

  const dateDisplay = datePrefix
    ? `${datePrefix} ${formattedDate}`
    : formattedDate;
  const transactionTitle = (
    <div style={{ display: "flex" }}>
      <div className={styles.transactionDialogTitle}>
        {displayDescription}
        {!postedDate && (
          <Tag
            label={l10n.getString(
              "transaction-status-pending",
              null,
              "Pending"
            )}
            kind="outline"
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onKeyUp={({ key }) => {
          if (key === "Enter") {
            setIsDialogOpen(true);
          }
        }}
        onClick={() => setIsDialogOpen(true)}
        className={"row-wrapper transaction-row clickable"}
      >
        <div className="row-items-column reverse">
          <div className="row-item">
            <div data-testid="transaction-title">{transactionTitle}</div>
            <div className="fontSize--s fontColor--secondary">
              {dateDisplay}
            </div>
          </div>
          {isSourceMultipleAccounts && (
            <div className="row-item account-name">
              {account?.nickname || account?.name}
            </div>
          )}
        </div>
        <div className="row-items-column">
          <div className="row-item align-right number">{displayedAmount}</div>
          {!isSourceMultipleAccounts && (
            <div className="row-item align-right number responsive-text-styling">
              {displayedResultingBalance}
            </div>
          )}
        </div>
      </div>
      <TransactionDetailsDialog
        transaction={transaction}
        transactionTitle={transactionTitle}
        displayedAmount={displayedAmount}
        displayedResultingBalance={displayedResultingBalance}
        isSourceMultipleAccounts={isSourceMultipleAccounts}
        isDialogOpen={isDialogOpen}
        closeDialog={() => setIsDialogOpen(false)}
        onDescriptionUpdate={setDisplayDescription}
        ReportIssueCallback={() => {
          setIsReportIssueDialogOpen(true);
        }}
      />
      <NewThreadDialog
        accountNumber={transaction.account.number}
        transactionId={transaction.id}
        isDialogOpen={isReportIssueDialogOpen}
        closeDialog={() => setIsReportIssueDialogOpen(false)}
        replyTime={replyTime}
      />
    </>
  );
};

interface TransactionTableProps extends EmptyTransactionStateProps {
  transactions: Transaction[];
  replyTime: string;
}

const TransactionTable = ({
  isSourceMultipleAccounts,
  transactions,
  replyTime
}: TransactionTableProps) => (
  /*
    table content slightly differs depending on whether transactions from 1 or 1+ accounts are displayed
    when `isSourceMultipleAccounts` is true, the headers are: DESCRIPTION | **ACCOUNT** | AMOUNT
    when `isSourceMultipleAccounts` is false, the headers are: DESCRIPTION | AMOUNT | **BALANCE**
   */
  <div
    className={cc([
      "card-table transactionTable no-table-top-border",
      { sparse: isSourceMultipleAccounts }
    ])}
  >
    <div className="row-wrapper">
      <div className="row-item-header">DESCRIPTION</div>
      {isSourceMultipleAccounts && (
        <div className="row-item-header">ACCOUNT</div>
      )}
      <div className="row-item-header align-right">AMOUNT</div>
      {!isSourceMultipleAccounts && (
        <div className="row-item-header align-right">BALANCE</div>
      )}
    </div>
    {transactions.map((t) => (
      <TransactionRow
        key={t.id}
        isSourceMultipleAccounts={isSourceMultipleAccounts}
        transaction={t}
        replyTime={replyTime}
      />
    ))}
  </div>
);

export const EmptyTransactionState = ({
  isSourceMultipleAccounts
}: EmptyTransactionStateProps) => (
  /* for the "wow such empty" state when there are no transactions */
  <div className="padding--y--l">
    <div className="fontWeight--bold padding--top--xs">
      You have no transactions
    </div>
    <div className="padding--top--xxs">
      Once you have {isSourceMultipleAccounts && "recent "}transaction activity,
      it&apos;ll show up here
    </div>
  </div>
);
export default TransactionTable;
